import React, { useState, useEffect } from 'react';
import MetadataDropdown from 'components/metadata/metadata-dropdown';
import { MetadataItemType } from 'pages/metadata/types';
import useEditShopContext from 'contexts/shop.context';
import { useParams } from 'react-router-dom';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import SingleBottomNav from 'components/base/single-bottom-nav';

type Props = {
  onEditComplete: () => void;
};
const EditShopMetaData = ({ onEditComplete }: Props) => {
  const {
    shopDetails,
    setShopDetails,
    businessMetadataList,
    descriptiveMetadataList,
    keywordsList,
    createMetadata
  } = useEditShopContext();

  const [selectedKeywords, setSelectedKeywords] = useState<MetadataItemType[]>([
    ...shopDetails.brand_keywords
  ]);
  const [selectedDescriptiveMetadata, setSelectedDescriptiveMetadata] = useState<
    MetadataItemType[]
  >([...shopDetails.brand_descriptive_metadatas]);
  const [selectedBusinessMetadata, setSelectedBusinessMetadata] = useState<MetadataItemType[]>([
    ...shopDetails.brand_business_metadatas
  ]);
  const [loading, setLoading] = useState(false);

  const { shop_id } = useParams();

  const handleUpdateShop = async () => {
    setLoading(true);
    const payload = {
      ...(shopDetails.brand_business_metadatas.length && {
        business_meta: shopDetails.brand_business_metadatas
      }),
      ...(shopDetails.brand_descriptive_metadatas.length && {
        descriptive_meta: shopDetails.brand_descriptive_metadatas
      }),
      ...(shopDetails.brand_keywords.length && { keyword_meta: shopDetails.brand_keywords })
    };
    try {
      await apiService.put(`shops/${shop_id}`, { data: payload });

      onEditComplete();
      toast(<ToastMessage type='success' message={`Shop updated successfully`} />);
    } catch {
      toast(<ToastMessage type='error' message={`There was a problem updating shop `} />);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setShopDetails({
      ...shopDetails,
      brand_descriptive_metadatas: selectedDescriptiveMetadata.map((metadata) => {
        return {
          name: metadata.name,
          id: metadata.id
        };
      }),
      brand_business_metadatas: selectedBusinessMetadata.map((metadata) => {
        return {
          name: metadata.name,
          id: metadata.id
        };
      }),
      brand_keywords: selectedKeywords.map((keyword) => {
        return {
          name: keyword.name,
          id: keyword.id
        };
      })
    });
  }, [selectedKeywords, selectedDescriptiveMetadata, selectedBusinessMetadata]);

  const onKeywordTagClick = (item: MetadataItemType) => {
    const exists = !!selectedKeywords.find((k) => k.id === item.id);
    if (!exists) {
      setSelectedKeywords([...selectedKeywords, item]);
    } else {
      setSelectedKeywords((words) => words.filter((w) => w.id !== item.id));
    }
  };
  const onDescriptiveTagClick = (item: MetadataItemType) => {
    const exists = !!selectedDescriptiveMetadata.find((k) => k.id === item.id);
    if (!exists) {
      setSelectedDescriptiveMetadata([...selectedDescriptiveMetadata, item]);
    } else {
      setSelectedDescriptiveMetadata((words) => words.filter((w) => w.id !== item.id));
    }
  };
  const onBusinessTagClick = (item: MetadataItemType) => {
    const exists = !!selectedBusinessMetadata.find((k) => k.id === item.id);
    if (!exists) {
      setSelectedBusinessMetadata([...selectedBusinessMetadata, item]);
    } else {
      setSelectedBusinessMetadata((words) => words.filter((w) => w.id !== item.id));
    }
  };

  return (
    <div>
      <MetadataDropdown
        title='Keywords'
        description='Select keyword'
        metadataList={keywordsList}
        selectedMetadata={shopDetails.brand_keywords}
        onCreate={(w) => createMetadata(w, 'brand')}
        onTagClick={onKeywordTagClick}
      />
      <MetadataDropdown
        title='Descriptive Metadata'
        description='Select descriptive metadata'
        metadataList={descriptiveMetadataList}
        selectedMetadata={shopDetails.brand_descriptive_metadatas}
        onCreate={(w) => createMetadata(w, 'descriptive')}
        onTagClick={onDescriptiveTagClick}
      />
      <MetadataDropdown
        title='Business Metadata'
        description='Select Business metadata'
        metadataList={businessMetadataList}
        selectedMetadata={shopDetails.brand_business_metadatas}
        onCreate={(w) => createMetadata(w, 'business')}
        onTagClick={onBusinessTagClick}
      />
      <div>
        <SingleBottomNav
          loading={loading}
          onConfirm={handleUpdateShop}
          backBtndisabled
          btnDisabled={loading}
        />
      </div>
    </div>
  );
};

export default EditShopMetaData;
